/**
 * PortfoliosPage — master-detail layout (Wave 3b).
 *
 * Left sidebar : search + portfolio list (cards), excluding aof1- records.
 * Right detail : header + version picker + meta row + sub-strategies table + recent runs table.
 *
 * Does NOT include "Run backtest" / "Rerun" buttons — backtesting is launched from
 * the RunBacktestModal (Rule 13: client-side compute only, no server-side dispatch here).
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { usePortfoliosStore } from '../../stores/portfoliosStore.js';
import { usePinnedAggregationsStore } from '../../stores/pinnedAggregationsStore.js';
import { usePortfolios, useAggregationVersions, useAggregationRuns, useAggregationPaperSessions, useAggregationOptimizations } from '../../hooks/useConfigurations.js';
import { RenamePortfolioModal } from './RenamePortfolioModal.js';
import { DeletePortfolioModal } from './DeletePortfolioModal.js';
import { useScrollToSelected } from '../../hooks/useScrollToSelected.js';
import { useQuery } from '@tanstack/react-query';
import { getAggregation } from '../../api/client.js';
import { Spinner } from '../Spinner/Spinner.js';
import type { AggregationConfig, BacktestSummary } from '../../types.js';
import type { PortfolioVersionSummary, StrategyOptimizationSummary, ConfigPaperSessionSummary } from '../../api/client.js';
import { usePaperTradingStore } from '../../stores/paperTradingStore.js';
import { useOptimizationsPageStore } from '../../stores/optimizationsPageStore.js';
import { useOpenedRunsStore } from '../../stores/openedRunsStore.js';
import { useConfigurationStore } from '../../stores/configurationStore.js';
import { useLoadBacktest } from '../../hooks/useBacktest.js';
import { useUserStrategies } from '../../hooks/useUserStrategies.js';
import { useConfigStore } from '../../stores/backtestStore.js';
import { RouteLink } from '../RouteLink/RouteLink.js';
import { optimizationsTabToSlug } from '../../hooks/useUrlSync.js';

// ============================================================================
// Helpers
// ============================================================================

function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

function formatMoney(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
}

function formatReturn(v: number): { text: string; cls: 'pos' | 'neg' | '' } {
  if (v > 0) return { text: `+${v.toFixed(1)}%`, cls: 'pos' };
  if (v < 0) return { text: `${v.toFixed(1)}%`, cls: 'neg' };
  return { text: `${v.toFixed(1)}%`, cls: '' };
}

function allocationBadgeClass(mode: string): string {
  if (mode === 'top_n') return 'sl-badge sl-badge-topn';
  if (mode === 'weighted_multi') return 'sl-badge sl-badge-weighted';
  return 'sl-badge sl-badge-single';
}

function allocationLabel(mode: string): string {
  if (mode === 'top_n') return 'top_n';
  if (mode === 'weighted_multi') return 'weighted';
  if (mode === 'single_strongest') return 'single';
  return mode;
}

// ============================================================================
// VersionPicker
// ============================================================================

interface VersionPickerProps {
  currentVersion: number;
  versions: PortfolioVersionSummary[];
}

/**
 * VersionPicker — read-only version history viewer.
 *
 * NOTE: This does NOT switch the displayed portfolio version. There is no
 * backend endpoint to fetch a versioned portfolio snapshot (unlike
 * user-strategy versions, which have `GET .../versions/:version`), so
 * "selecting" an entry here cannot actually change what's shown below. The
 * list previously accepted an `onSelect` callback that was always wired to a
 * no-op, which looked like a broken/dead feature. It's now presented plainly
 * as a history list — no click affordance implying a switch will happen.
 */
function VersionPicker({ currentVersion, versions }: VersionPickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          border: '1px solid var(--sl-border-2)',
          borderRadius: 8,
          padding: '5px 10px',
          fontSize: 12,
          color: 'var(--sl-tx-dim)',
          fontFamily: 'var(--sl-mono)',
          background: 'transparent',
          cursor: 'pointer',
        }}
        title="Version history (read-only)"
      >
        v{currentVersion} ▾
      </button>
      {open && versions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          left: 0,
          minWidth: 280,
          background: 'var(--sl-elev)',
          border: '1px solid var(--sl-border-2)',
          borderRadius: 10,
          boxShadow: '0 20px 50px -20px #000',
          zIndex: 12,
          overflow: 'hidden',
        }}>
          <div style={{
            padding: '7px 12px',
            fontSize: 10.5,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--sl-tx-faint)',
            borderBottom: '1px solid var(--sl-border)',
          }}>
            Version history
          </div>
          {[...versions].reverse().map((v) => {
            const isCur = v.version === currentVersion;
            return (
              <div
                key={v.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 9,
                  padding: '9px 12px',
                  borderBottom: '1px solid var(--sl-border)',
                  background: isCur ? 'var(--sl-accent-bg)' : 'transparent',
                }}
              >
                <span style={{ fontFamily: 'var(--sl-mono)', fontWeight: 600, fontSize: 12.5, color: 'var(--sl-tx)', minWidth: 44 }}>
                  v{v.version}
                </span>
                <span style={{ color: 'var(--sl-tx-dim)', fontSize: 11.5, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {v.changeSummary ?? v.name}
                </span>
                <span style={{ color: 'var(--sl-tx-faint)', fontSize: 11, fontFamily: 'var(--sl-mono)' }}>
                  {timeAgo(v.createdAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PortfolioCard — sidebar card
// ============================================================================

// SHA-43 portfolio CRUD: action button style (mirrors UserStrategyCard)
const portfolioActionBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '2px 4px',
  lineHeight: 1,
  fontSize: 13,
  color: '#777',
  flexShrink: 0,
};

interface PortfolioCardProps {
  portfolio: AggregationConfig;
  isSelected: boolean;
  onClick: () => void;
  /** Whether this portfolio is pinned (SHA-43) — pinned cards float to the top. */
  isPinned?: boolean;
  /** Toggle pin state. When omitted, the pin button is not rendered. */
  onTogglePin?: () => void;
  /** Open the rename flow. When omitted, the rename button is not rendered. */
  onRename?: () => void;
  /** Open the delete-confirm flow. When omitted, the delete button is not rendered. */
  onDelete?: () => void;
}

function PortfolioCard({
  portfolio,
  isSelected,
  onClick,
  isPinned = false,
  onTogglePin,
  onRename,
  onDelete,
}: PortfolioCardProps) {
  const label = allocationLabel(portfolio.allocationMode);
  const badgeCls = allocationBadgeClass(portfolio.allocationMode);
  const hasTopn = portfolio.allocationMode === 'top_n';
  const hasWeighted = portfolio.allocationMode === 'weighted_multi';

  return (
    <RouteLink
      data-item-id={portfolio.id}
      href={`/portfolios/${encodeURIComponent(portfolio.id)}`}
      onNavigate={onClick}
      className={`card${isSelected ? ' sel' : ''}`}
      style={{ display: 'block', width: '100%', textAlign: 'left', textDecoration: 'none', color: 'inherit' }}
    >
      <div className="top">
        {isPinned && (
          <span aria-label="pinned" title="Pinned" style={{ fontSize: 11, color: '#facc15', flexShrink: 0, marginRight: 4 }}>
            &#9733;
          </span>
        )}
        <span className="nm" style={{ fontFamily: 'var(--sl-ui)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {portfolio.name}
        </span>
        <span className="sl-badge sl-badge-ver" style={{ marginLeft: 6, flexShrink: 0 }}>
          v{portfolio.currentVersion ?? '?'}
        </span>
        {/* SHA-43 CRUD action buttons */}
        {onTogglePin && (
          <button
            type="button"
            aria-label={isPinned ? 'Unpin portfolio' : 'Pin portfolio'}
            title={isPinned ? 'Unpin' : 'Pin'}
            style={{ ...portfolioActionBtnStyle, color: isPinned ? '#facc15' : '#777' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onTogglePin();
            }}
          >
            {isPinned ? '★' : '☆'}
          </button>
        )}
        {onRename && (
          <button
            type="button"
            aria-label="Rename portfolio"
            title="Rename"
            style={portfolioActionBtnStyle}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRename();
            }}
          >
            &#9998;
          </button>
        )}
        {onDelete && (
          <button
            type="button"
            aria-label="Delete portfolio"
            title="Delete"
            style={{ ...portfolioActionBtnStyle, color: '#e57373' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
          >
            &#128465;
          </button>
        )}
      </div>
      <div className="sub" style={{ marginTop: 7, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className={badgeCls}>
          {(hasTopn || hasWeighted) && <span className="sl-dot" />}
          {portfolio.subStrategies.length} sym · {label}
        </span>
      </div>
    </RouteLink>
  );
}

// ============================================================================
// PortfolioSidebar
// ============================================================================

function PortfolioSidebar() {
  const { selectedPortfolioId, setSelectedPortfolioId, searchQuery, setSearchQuery } = usePortfoliosStore();
  const { data: portfolios, isLoading, error } = usePortfolios();
  const { pinned, isPinned, toggle: togglePin } = usePinnedAggregationsStore();
  const listRef = useRef<HTMLDivElement>(null);
  useScrollToSelected(selectedPortfolioId, listRef);

  // SHA-43 portfolio CRUD: rename/delete modal targets
  const [renameTarget, setRenameTarget] = useState<AggregationConfig | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AggregationConfig | null>(null);

  const searched = portfolios?.filter((p) => {
    const q = searchQuery.toLowerCase();
    return p.name.toLowerCase().includes(q);
  }) ?? [];

  // SHA-43: pinned portfolios float to the top, most-recently-pinned first.
  // Non-pinned portfolios keep their original (server) order.
  const filtered = [...searched].sort((a, b) => {
    const aPinIdx = pinned.indexOf(a.id);
    const bPinIdx = pinned.indexOf(b.id);
    const aPinned = aPinIdx !== -1;
    const bPinned = bPinIdx !== -1;
    if (aPinned && bPinned) return aPinIdx - bPinIdx;
    if (aPinned) return -1;
    if (bPinned) return 1;
    return 0;
  });

  return (
    <aside style={{
      width: 330,
      height: '100%',
      flexShrink: 0,
      borderRight: '1px solid var(--sl-border)',
      background: 'var(--sl-elev)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <div style={{ padding: '14px 12px 0', flexShrink: 0 }}>
        {/* Search */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--sl-surface)',
          border: '1px solid var(--sl-border)',
          borderRadius: 10,
          padding: '8px 11px',
          color: 'var(--sl-tx-faint)',
          fontSize: 13,
          marginBottom: 12,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4-4" />
          </svg>
          <input
            type="text"
            placeholder="Search portfolios…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ all: 'unset', flex: 1, color: 'var(--sl-tx)', fontSize: 13, fontFamily: 'var(--sl-ui)' }}
          />
        </div>
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '0 12px 40px' }}>
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spinner size="lg" className="text-gray-400" />
          </div>
        )}
        {error && (
          <div style={{ margin: 12, padding: '10px 12px', background: 'rgba(242,104,95,0.1)', border: '1px solid #7f1d1d', borderRadius: 8, color: '#fca5a5', fontSize: 13 }}>
            Failed to load portfolios
          </div>
        )}
        {!isLoading && filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: 'var(--sl-tx-faint)', fontSize: 13, padding: '24px 0' }}>
            {searchQuery ? 'No portfolios match.' : 'No portfolios yet.'}
          </p>
        )}
        {filtered.map((p) => (
          <PortfolioCard
            key={p.id}
            portfolio={p}
            isSelected={p.id === selectedPortfolioId}
            onClick={() => setSelectedPortfolioId(p.id === selectedPortfolioId ? null : p.id)}
            isPinned={isPinned(p.id)}
            onTogglePin={() => togglePin(p.id)}
            onRename={() => setRenameTarget(p)}
            onDelete={() => setDeleteTarget(p)}
          />
        ))}
      </div>

      {/* Footer stats */}
      {filtered.length > 0 && (
        <div
          style={{
            padding: '8px 16px',
            borderTop: '1px solid var(--sl-border)',
            fontSize: 12,
            color: 'var(--sl-tx-ghost)',
          }}
        >
          {filtered.length} portfolio{filtered.length !== 1 ? 's' : ''}
          {searchQuery && portfolios && ` (${portfolios.length} total)`}
        </div>
      )}

      {renameTarget && (
        <RenamePortfolioModal
          isOpen={true}
          portfolioId={renameTarget.id}
          currentName={renameTarget.name}
          onClose={() => setRenameTarget(null)}
        />
      )}

      {deleteTarget && (
        <DeletePortfolioModal
          isOpen={true}
          portfolioId={deleteTarget.id}
          portfolioName={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            // The useDeleteAggregation hook clears selectedPortfolioId automatically
            // when the deleted portfolio was selected — no extra action needed here.
          }}
        />
      )}
    </aside>
  );
}

// ============================================================================
// SubStrategiesTable
// ============================================================================

interface SubStrategiesTableProps {
  portfolio: AggregationConfig;
  /** Called with the row index when a resolvable sub-strategy row is clicked. */
  onOpenSubStrategy: (index: number) => void;
  /**
   * Parallel to subStrategies — the resolved user_strategy id for that row, or
   * undefined when the strategy name couldn't be resolved (row stays
   * non-interactive). Used both for the click affordance AND to build the
   * SHA-39 real `<a href>` — the earlier plain-boolean array only supported
   * the JS onClick path, not a real navigable href.
   */
  subStrategyIds: (string | undefined)[];
}

function SubStrategiesTable({ portfolio, onOpenSubStrategy, subStrategyIds }: SubStrategiesTableProps) {
  const subs = portfolio.subStrategies;
  const versions = portfolio.subStrategyVersions ?? [];

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '22px 1.3fr 104px 1.4fr 24px',
        alignItems: 'center',
        gap: 12,
        padding: '8px 14px',
        background: 'var(--sl-surface-2)',
        color: 'var(--sl-tx-faint)',
        fontSize: 10.5,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 600,
        fontFamily: 'var(--sl-ui)',
      }}>
        <span>#</span>
        <span>Strategy</span>
        <span>Pair</span>
        <span>Params</span>
        <span />
      </div>
      {subs.map((sub, i) => {
        const ver = versions[i];
        const paramStr = sub.params
          ? Object.entries(sub.params).map(([k, v]) => `${k} ${v}`).join(' · ')
          : '';
        const paramWithTf = [paramStr, sub.timeframe].filter(Boolean).join(' · ');
        const resolvedId = subStrategyIds[i];
        const clickable = resolvedId != null;

        const rowContent = (
          <>
            <span style={{ color: 'var(--sl-tx-ghost)', fontFamily: 'var(--sl-mono)', fontSize: 12 }}>{i + 1}</span>
            <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--sl-tx)' }}>
              {sub.strategyName}
              {ver != null && (
                <span className="sl-badge sl-badge-ver" style={{ marginLeft: 6 }}>v{ver}</span>
              )}
            </span>
            <span className="sl-badge sl-badge-sym">{sub.symbol}</span>
            <span style={{ color: 'var(--sl-tx-dim)', fontSize: 11.5, fontFamily: 'var(--sl-mono)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {paramWithTf}
            </span>
            <span style={{ color: clickable ? 'var(--sl-tx-dim)' : 'var(--sl-tx-ghost)' }}>›</span>
          </>
        );

        const rowStyle: React.CSSProperties = {
          display: 'grid',
          gridTemplateColumns: '22px 1.3fr 104px 1.4fr 24px',
          alignItems: 'center',
          gap: 12,
          padding: '11px 14px',
          borderTop: '1px solid var(--sl-border)',
          transition: '0.12s',
          cursor: clickable ? 'pointer' : 'default',
          textDecoration: 'none',
          color: 'inherit',
        };

        if (clickable) {
          return (
            <RouteLink
              key={i}
              href={`/strategies/${encodeURIComponent(resolvedId)}`}
              onNavigate={() => onOpenSubStrategy(i)}
              style={rowStyle}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sl-surface)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
            >
              {rowContent}
            </RouteLink>
          );
        }

        return (
          <div key={i} style={rowStyle}>
            {rowContent}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// RecentRunsTable
// ============================================================================

interface RecentRunsTableProps {
  portfolioId: string;
  onOpenRun: (run: BacktestSummary) => void;
}

function RecentRunsTable({ portfolioId, onOpenRun }: RecentRunsTableProps) {
  const { data: runs, isLoading } = useAggregationRuns(portfolioId);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sl-tx-faint)', fontSize: 13 }}>
        <Spinner size="sm" className="text-gray-600" />
        Loading runs...
      </div>
    );
  }

  if (!runs || runs.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
        No backtest runs yet.
      </p>
    );
  }

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 76px 60px 78px 54px',
        alignItems: 'center',
        gap: 12,
        padding: '8px 14px',
        background: 'var(--sl-surface-2)',
        color: 'var(--sl-tx-faint)',
        fontSize: 10.5,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 600,
        fontFamily: 'var(--sl-ui)',
      }}>
        <span>Run</span>
        <span>Composition</span>
        <span style={{ textAlign: 'right' }}>Return</span>
        <span style={{ textAlign: 'right' }}>Sharpe</span>
        <span style={{ textAlign: 'right' }}>Max DD</span>
        <span style={{ textAlign: 'right' }}>Ran</span>
      </div>
      {runs.map((run) => {
        const ret = formatReturn(run.totalReturnPercent);
        const dd = run.maxDrawdownPercent != null ? formatReturn(-run.maxDrawdownPercent) : null;
        // Derive symbol count from aggregationName or perAssetResults
        const symCount = run.symbol === 'MULTI' ? (run.strategyVersions ? Object.keys(run.strategyVersions).length : undefined) : 1;

        return (
          <RunRow key={run.id} run={run} ret={ret} dd={dd} symCount={symCount} onOpenRun={onOpenRun} />
        );
      })}
    </div>
  );
}

interface RunRowProps {
  run: BacktestSummary;
  ret: { text: string; cls: 'pos' | 'neg' | '' };
  dd: { text: string; cls: 'pos' | 'neg' | '' } | null;
  symCount: number | undefined;
  onOpenRun: (run: BacktestSummary) => void;
}

function RunRow({ run, ret, dd, symCount, onOpenRun }: RunRowProps) {
  const numStyle: React.CSSProperties = {
    fontFamily: 'var(--sl-mono)',
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    fontSize: 12.5,
  };

  const runLabel = run.aggregationName
    ? `${run.aggregationName}`
    : run.strategyName;

  return (
    <RouteLink
      href={`/backtesting/${encodeURIComponent(run.id)}`}
      onNavigate={() => onOpenRun(run)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 76px 60px 78px 54px',
        alignItems: 'center',
        gap: 12,
        padding: '11px 14px',
        borderTop: '1px solid var(--sl-border)',
        transition: '0.12s',
        cursor: 'pointer',
        textDecoration: 'none',
        color: 'inherit',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sl-surface)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
    >
      <span style={{ fontSize: 12.5, color: 'var(--sl-tx)', fontFamily: 'var(--sl-ui)' }}>
        {runLabel}
      </span>
      <span>
        {symCount != null ? (
          <span className="sl-badge sl-badge-multi">{symCount} sym</span>
        ) : (
          <span className="sl-badge sl-badge-sym">{run.symbol}</span>
        )}
      </span>
      <span style={{ ...numStyle, color: ret.cls === 'pos' ? 'var(--sl-pos)' : ret.cls === 'neg' ? 'var(--sl-neg)' : 'var(--sl-tx)' }}>
        {ret.text}
      </span>
      <span style={{ ...numStyle, color: 'var(--sl-tx)' }}>
        {run.sharpeRatio.toFixed(2)}
      </span>
      <span style={{ ...numStyle, color: dd?.cls === 'neg' ? 'var(--sl-neg)' : 'var(--sl-tx)' }}>
        {dd ? dd.text : '—'}
      </span>
      <span style={{ ...numStyle, color: 'var(--sl-tx-faint)' }}>
        {timeAgo(typeof run.runAt === 'string' ? new Date(run.runAt).getTime() : run.runAt)}
      </span>
    </RouteLink>
  );
}

// ============================================================================
// PortfolioOptimizationsSection
// ============================================================================

function fmtOptShort(n: number): string {
  return n.toFixed(2);
}

function fmtOptDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

interface PortfolioOptimizationsSectionProps {
  portfolioId: string;
  onNavigateToOptimization: (id: string, type: 'grid_search' | 'walk_forward') => void;
}

function PortfolioOptimizationsSection({ portfolioId, onNavigateToOptimization }: PortfolioOptimizationsSectionProps) {
  const { data: optimizations, isLoading } = useAggregationOptimizations(portfolioId);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sl-tx-faint)', fontSize: 13 }}>
        <Spinner size="sm" className="text-gray-600" />
        Loading...
      </div>
    );
  }

  if (!optimizations || optimizations.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
        No optimizations yet. Run a grid search or walk-forward on this portfolio to see results here.
      </p>
    );
  }

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {optimizations.map((opt: StrategyOptimizationSummary) => (
        <RouteLink
          key={opt.id}
          href={`/optimizations/${optimizationsTabToSlug(opt.type)}/${encodeURIComponent(opt.id)}`}
          onNavigate={() => onNavigateToOptimization(opt.id, opt.type)}
          style={{
            display: 'grid',
            gridTemplateColumns: '70px 90px 50px 1fr auto',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            borderBottom: '1px solid var(--sl-border)',
            cursor: 'pointer',
            transition: '0.12s',
            textDecoration: 'none',
            color: 'inherit',
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sl-surface)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
        >
          <span className={opt.type === 'grid_search' ? 'sl-badge sl-badge-topn' : 'sl-badge sl-badge-weighted'}>
            {opt.type === 'grid_search' ? 'GS' : 'WF'}
          </span>
          <span className="sl-badge sl-badge-sym">{opt.symbol.replace('/USDT:USDT', '').replace('/USDT', '')}</span>
          <span style={{ fontFamily: 'var(--sl-mono)', fontSize: 12, color: 'var(--sl-tx-dim)' }}>
            {opt.timeframe}
          </span>
          <span style={{
            fontFamily: 'var(--sl-mono)',
            fontSize: 12,
            color: opt.sharpeRatio >= 1 ? 'var(--sl-pos)' : opt.sharpeRatio >= 0 ? 'var(--sl-tx-dim)' : 'var(--sl-neg)',
            fontWeight: 600,
          }}>
            {fmtOptShort(opt.sharpeRatio)}
          </span>
          <span style={{ fontSize: 11, color: 'var(--sl-tx-faint)', fontFamily: 'var(--sl-mono)' }}>
            {fmtOptDate(opt.optimizedAt)}
          </span>
        </RouteLink>
      ))}
    </div>
  );
}

// ============================================================================
// PortfolioPaperSessionsSection
// ============================================================================

const STATUS_COLORS: Record<string, string> = {
  running: 'var(--sl-pos)',
  paused: '#F59E0B',
  stopped: 'var(--sl-tx-dim)',
  error: 'var(--sl-neg)',
};

interface PortfolioPaperSessionsSectionProps {
  portfolioId: string;
  onNavigateToSession: (sessionId: string) => void;
}

function PortfolioPaperSessionsSection({ portfolioId, onNavigateToSession }: PortfolioPaperSessionsSectionProps) {
  const { data: sessions, isLoading } = useAggregationPaperSessions(portfolioId);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sl-tx-faint)', fontSize: 13 }}>
        <Spinner size="sm" className="text-gray-600" />
        Loading...
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <p style={{ fontSize: 13, color: 'var(--sl-tx-faint)', margin: 0 }}>
        No paper sessions yet. Create one from the Trading page.
      </p>
    );
  }

  return (
    <div style={{ border: '1px solid var(--sl-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--sl-elev)' }}>
      {sessions.map((session: ConfigPaperSessionSummary) => {
        const statusColor = STATUS_COLORS[session.status] ?? 'var(--sl-tx-dim)';
        const ret = ((session.currentEquity - session.initialCapital) / session.initialCapital) * 100;
        const retText = ret >= 0 ? `+${ret.toFixed(1)}%` : `${ret.toFixed(1)}%`;
        const retColor = ret >= 0 ? 'var(--sl-pos)' : 'var(--sl-neg)';

        return (
          <RouteLink
            key={session.id}
            href={`/paper-trading/${encodeURIComponent(session.id)}`}
            onNavigate={() => onNavigateToSession(session.id)}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 80px 80px auto',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderBottom: '1px solid var(--sl-border)',
              cursor: 'pointer',
              transition: '0.12s',
              textDecoration: 'none',
              color: 'inherit',
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'var(--sl-surface)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = 'transparent'; }}
          >
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--sl-tx)' }}>
              {session.name}
            </span>
            <span style={{ fontSize: 11.5, color: statusColor, fontWeight: 600, textTransform: 'capitalize', fontFamily: 'var(--sl-mono)' }}>
              {session.status}
            </span>
            <span style={{
              fontFamily: 'var(--sl-mono)',
              fontSize: 12,
              color: retColor,
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'right',
            }}>
              {retText}
            </span>
            <span style={{ color: 'var(--sl-tx-ghost)', fontSize: 13 }}>›</span>
          </RouteLink>
        );
      })}
    </div>
  );
}

// ============================================================================
// PortfolioDetail
// ============================================================================

interface PortfolioDetailProps {
  portfolioId: string;
}

function PortfolioDetail({ portfolioId }: PortfolioDetailProps) {
  const { data: portfolio, isLoading, error } = useQuery({
    queryKey: ['aggregation', portfolioId],
    queryFn: () => getAggregation(portfolioId),
    staleTime: 30_000,
  });
  const { data: versions = [] } = useAggregationVersions(portfolioId);

  // Navigation callbacks for Optimizations section
  const setSelectedOptimizationId = useOptimizationsPageStore((s) => s.setSelectedOptimizationId);
  const setActiveOptimizationsTab = useOptimizationsPageStore((s) => s.setActiveTab);
  const { setActivePage, setSelectedSession } = usePaperTradingStore();

  // BUG 1: Recent runs — open a run in the Backtesting page
  const { loadBacktest } = useLoadBacktest();
  const markOpened = useOpenedRunsStore((s) => s.markOpened);

  const handleOpenRun = useCallback(async (run: BacktestSummary) => {
    markOpened(run);
    // Navigate immediately so the user sees the backtesting page while the run loads
    setActivePage('backtesting');
    const result = await loadBacktest(run.id);
    if (result) {
      useConfigStore.getState().applyHistoryParams(result);
    }
  }, [loadBacktest, markOpened, setActivePage]);

  // BUG 2: Sub-strategies — open the owning user_strategy (and its config) in the Strategies page
  const { data: userStrategies } = useUserStrategies();
  const setSelectedStrategyId = useConfigurationStore((s) => s.setSelectedStrategyId);
  const setSelectedConfigId = useConfigurationStore((s) => s.setSelectedConfigId);

  const handleOpenSubStrategy = useCallback((index: number) => {
    if (!portfolio) return;
    const sub = portfolio.subStrategies[index];
    const configId = portfolio.subStrategyConfigIds?.[index];
    const userStrategy = userStrategies?.find((us) => us.name === sub.strategyName);
    if (!userStrategy) return; // Strategy not resolvable — row is non-interactive
    setSelectedStrategyId(userStrategy.id);
    // setSelectedStrategyId clears selectedConfigId; set it after so both land in one render
    if (configId) {
      setSelectedConfigId(configId);
    }
    setActivePage('configurations');
  }, [portfolio, userStrategies, setSelectedStrategyId, setSelectedConfigId, setActivePage]);

  const handleNavigateToOptimization = (id: string, type: 'grid_search' | 'walk_forward') => {
    setSelectedOptimizationId(id);
    setActiveOptimizationsTab(type);
    setActivePage('optimizations');
  };

  const handleNavigateToSession = (sessionId: string) => {
    setSelectedSession(sessionId);
    setActivePage('paper-trading');
  };

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="lg" className="text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--sl-neg)', fontSize: 14 }}>
        Failed to load portfolio.
      </div>
    );
  }

  if (!portfolio) return null;

  const currentVersion = portfolio.currentVersion ?? (versions.length > 0 ? Math.max(...versions.map((v) => v.version)) : 1);
  // NOTE: portfolio.currentVersion may be undefined on legacy responses; derive from versions as fallback
  const badgeCls = allocationBadgeClass(portfolio.allocationMode);
  const hasTopn = portfolio.allocationMode === 'top_n';
  const hasWeighted = portfolio.allocationMode === 'weighted_multi';

  // BUG 2: Resolve each sub-strategy row to its user_strategy id (for click
  // affordance AND to build the SHA-39 real `<a href>`); undefined = not
  // resolvable, row stays non-interactive.
  const subStrategyIds = portfolio.subStrategies.map(
    (sub) => userStrategies?.find((us) => us.name === sub.strategyName)?.id,
  );

  return (
    <div style={{ padding: '20px 24px 60px', overflowY: 'auto', flex: 1, fontFamily: 'var(--sl-ui)', color: 'var(--sl-tx)' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', margin: 0 }}>
          {portfolio.name}
        </h1>
        <VersionPicker
          currentVersion={currentVersion}
          versions={versions}
        />
      </div>

      {/* Meta row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        color: 'var(--sl-tx-dim)',
        fontSize: 12.5,
        margin: '10px 0 20px',
        flexWrap: 'wrap',
      }}>
        <span className={badgeCls}>
          {(hasTopn || hasWeighted) && <span className="sl-dot" />}
          {allocationLabel(portfolio.allocationMode)}
        </span>
        <SepdotSpan />
        <span>max {portfolio.maxPositions} positions</span>
        <SepdotSpan />
        <span style={{ fontFamily: 'var(--sl-mono)' }}>{formatMoney(portfolio.initialCapital)}</span>
        <SepdotSpan />
        <span>{portfolio.exchange} · perp</span>
      </div>

      {/* Sub-strategies */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label="Sub-strategies" count={portfolio.subStrategies.length} />
        <SubStrategiesTable
          portfolio={portfolio}
          onOpenSubStrategy={handleOpenSubStrategy}
          subStrategyIds={subStrategyIds}
        />
      </div>

      {/* Recent runs */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label="Recent runs" />
        <RecentRunsTable portfolioId={portfolioId} onOpenRun={handleOpenRun} />
      </div>

      {/* Optimizations (GS + WF runs owned by this portfolio — migration 065) */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label="Optimizations" />
        <PortfolioOptimizationsSection
          portfolioId={portfolioId}
          onNavigateToOptimization={handleNavigateToOptimization}
        />
      </div>

      {/* Paper sessions running on this portfolio */}
      <div style={{ marginBottom: 24 }}>
        <SectionHeader label="Paper sessions" />
        <PortfolioPaperSessionsSection
          portfolioId={portfolioId}
          onNavigateToSession={handleNavigateToSession}
        />
      </div>
    </div>
  );
}

// ============================================================================
// Shared small components
// ============================================================================

function SepdotSpan() {
  return (
    <span style={{
      width: 3,
      height: 3,
      borderRadius: '50%',
      background: 'var(--sl-tx-ghost)',
      display: 'inline-block',
      flexShrink: 0,
    }} />
  );
}

function SectionHeader({ label, count }: { label: string; count?: number }) {
  return (
    <h3 style={{
      fontSize: 11,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: 'var(--sl-tx-faint)',
      fontWeight: 600,
      margin: '0 0 11px',
      display: 'flex',
      alignItems: 'center',
      gap: 9,
      fontFamily: 'var(--sl-ui)',
    }}>
      {label}
      {count != null && <span style={{ color: 'var(--sl-tx-ghost)' }}>{count}</span>}
      <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, var(--sl-border), transparent)' }} />
    </h3>
  );
}

// ============================================================================
// DetailEmptyState
// ============================================================================

function DetailEmptyState() {
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--sl-tx-faint)',
      fontSize: 14,
      gap: 8,
      fontFamily: 'var(--sl-ui)',
    }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      </svg>
      <p style={{ margin: 0 }}>Select a portfolio to view details</p>
    </div>
  );
}

// ============================================================================
// PortfoliosPage — root export
// ============================================================================

// Sidebar CSS (reusing mockup card / badge classes from index.css Wave 3a tokens)
const sidebarCSS = `
.card{display:block;width:100%;text-align:left;border:1px solid var(--sl-border);background:var(--sl-surface);border-radius:var(--sl-r);padding:11px 12px;margin-bottom:8px;position:relative;transition:.14s;cursor:pointer}
.card:hover{border-color:var(--sl-border-2);background:var(--sl-surface-2)}
.card.sel{border-color:var(--sl-accent-line);background:linear-gradient(180deg,var(--sl-accent-bg),transparent 90%)}
.card.sel::before{content:"";position:absolute;left:0;top:10px;bottom:10px;width:3px;border-radius:3px;background:var(--sl-accent);box-shadow:0 0 10px var(--sl-accent-line)}
.card .top{display:flex;align-items:center;gap:8px}
.card .nm{font-weight:600;font-size:13.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--sl-tx)}
.card .sub{display:flex;align-items:center;gap:8px;margin-top:7px;font-size:11.5px;color:var(--sl-tx-dim)}
`;

export function PortfoliosPage() {
  const { selectedPortfolioId } = usePortfoliosStore();

  return (
    <>
      <style>{sidebarCSS}</style>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '330px 1fr',
        height: '100%',
        overflow: 'hidden',
        background: 'var(--sl-bg)',
      }}>
        <PortfolioSidebar />
        <div style={{ overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          {selectedPortfolioId ? (
            <PortfolioDetail portfolioId={selectedPortfolioId} />
          ) : (
            <DetailEmptyState />
          )}
        </div>
      </div>
    </>
  );
}

export default PortfoliosPage;
